import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import type { ResolvedLengthUnit, ResolvedWeightUnit } from '../../../../shared/auth/types';
import type { DeletionImpact } from '@/generated/api/models/deletionImpact';
import type { ProductListFilterInput } from '@/generated/api/models/productListFilterInput';
import { productCategoryKeys } from './useProductCategories';
import { productCatalogLayerKeys } from './productCatalogLayerKeys';
import type { Product, ProductType, ProductStatus, ProductRole } from '../types';
// The variant envelopes are the generated response models the six typed variant routes
// declare (list / create / create-flat / generate / resolve / update) — single-sourced,
// so a shape change on the backend breaks compilation here instead of silently at runtime.
import type { ProductVariantListResponse } from '../../../../generated/api/models/productVariantListResponse';
import type { ProductVariantResponse } from '../../../../generated/api/models/productVariantResponse';
import type { Uom } from '../../../../generated/config-enums/uom';

interface ProductCreate {
  name: string;
  type: ProductType;
  // Optional — the backend defaults a blank/omitted value to 'unit'.
  base_unit_name?: string;
  /** Base unit token; omitted ⇒ the permissive legacy 'unit'. Fixes whole-vs-fractional entry. */
  base_uom?: Uom;
  product_category_ids?: string[];
  sku?: string | null;
  description?: string | null;
  /** Brand / manufacturer (Shopify "Vendor"). */
  brand?: string | null;
  base_price?: number | null;
  cost_price?: number | null;
  billing_cycle?: string | null;
  pricing_model?: string | null;
  service_billing_model?: string | null;
  service_duration_hours?: number | null;
  tax_category?: string | null;
  url?: string | null;
  hs_code?: string | null;
  country_of_origin?: string | null;
  image_id?: string | null;
  // Physical product dimensions (stored in metric)
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  // Per-record unit overrides (Runtime Units of Measure, Tier A).
  weight_unit?: ResolvedWeightUnit | null;
  dimension_unit?: ResolvedLengthUnit | null;
  status?: ProductStatus;
  /** Buy/sell/both. Omitted on manual create ⇒ backend defaults to 'sell'. */
  product_role?: ProductRole;
  company_ids?: string[];
  /** Tenant-defined custom fields (P4); validated server-side against definitions. */
  custom_fields?: Record<string, unknown>;
  /**
   * Sourcing reference links. A whole-set field, unlike `product_category_ids`
   * below: this list has exactly one editor (the product's Sourcing & Logistics
   * tab) and no incremental writer, so there is no concurrent append for a
   * rebind to discard. Omitted = leave unchanged; `[]` = remove them all.
   */
  reference_links?: string[];
}

interface ProductUpdate extends Partial<Omit<ProductCreate, 'product_category_ids'>> {
  /**
   * Category membership is an INCREMENTAL delta on update — the whole array is
   * not writable here. It has a second writer (bulk categorisation from the
   * category page, a Shopify re-import), so a client sending the array it read
   * on page load would silently discard every membership added since. Build
   * these with `categoryMembershipDelta` / `singleCategoryDelta`.
   */
  add_product_category_ids?: string[];
  remove_product_category_ids?: string[];
  clear_image?: boolean; // Set to true to remove the product's image
}

interface VariantCreate {
  name?: string | null;
  sku?: string | null;
  /** One dimension value per active product dimension. */
  option_value_ids?: string[];
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  /** Per-variant unit cost (Shopify "Cost per item"). */
  cost?: number | null;
  /** Per-variant MSRP / list price (Shopify "Compare At Price"). */
  compare_at_price?: number | null;
  /** Per-variant GTIN/UPC/EAN/ISBN barcode. */
  barcode?: string | null;
  image_id?: string | null;
  status?: string | null;
}

/** Update adds `clear_image` to detach the variant's image (image_id=null is a no-op server-side). */
type VariantUpdate = Partial<VariantCreate> & { clear_image?: boolean };

/** A single combination for batch variant generation. */
export interface VariantCombination {
  value_ids: string[];
}

export interface ListProductsParams {
  category_id?: string;
  type?: ProductType;
  /**
   * Drop one product type from the result. The catalog table hides `bundle`
   * (bundles have their own tab) — a negative filter the server has to resolve,
   * because a client-side slice on a server-paged list shows fewer rows than the
   * pager counted (ui-list-pages.md § 11).
   */
  exclude_type?: ProductType;
  /** Buy/sell filter. 'sell' shows sold + both, 'buy' shows purchased + both. */
  product_role?: ProductRole;
  status?: ProductStatus;
  search?: string;
  includeCategories?: boolean;
  /** Server-side sort column. Unknown values fall back to `name` server-side. */
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  /** Page window. Omit both for the unbounded picker fetch. */
  limit?: number;
  offset?: number;
  /** JSON object of custom-field filters, e.g. '{"material":"steel"}' (P5). */
  custom_fields?: string;
  /** Comma-separated tag UUIDs (cross-cutting tags framework). */
  tags?: string;
  /** Tag matching mode: 'any' (OR, default) or 'all' (AND). */
  tags_mode?: 'any' | 'all';
  /** `'own'` when a Full-Access admin narrows a governed list to their own
   * owner/shared scope via `useAdminListScope`; undefined for everyone else. */
  admin_scope?: string;
  /**
   * Browse ONE company's catalog. A document line editor passes the DOCUMENT's
   * own company, not the app bar's: a document raised for company A may not name
   * company B's product, so offering one only defers the refusal to save.
   * Products carrying no company tag are shared and stay visible. Omit it to
   * keep the app-bar active-company scope every other list uses — including a
   * read that CLASSIFIES a product some record already names, which must stay
   * able to see one outside that company.
   */
  company_id?: string;
}

export const productKeys = {
  all: ['products'] as const,
  list: (params?: ListProductsParams) => [...productKeys.all, 'list', params] as const,
  detail: (id: string) => [...productKeys.all, 'detail', id] as const,
  variants: (productId: string) => [...productKeys.all, productId, 'variants'] as const,
  dimensions: (productId: string) => [...productKeys.all, productId, 'dimensions'] as const,
  dimensionLibrary: () => [...productKeys.all, 'dimension-library'] as const,
  configurations: (productId: string) => [...productKeys.all, productId, 'configurations'] as const,
  categoryConfigurations: (categoryId: string) =>
    [...productKeys.all, 'category', categoryId, 'configurations'] as const,
};

function cleanParams(params?: ListProductsParams): ListProductsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListProductsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/** The one place the products list query string is built — rows and window alike. */
function buildProductListUrl(params?: ListProductsParams): string {
  const queryParams = new URLSearchParams();
  if (params?.category_id) queryParams.append('category_id', params.category_id);
  if (params?.type) queryParams.append('type', params.type);
  if (params?.exclude_type) queryParams.append('exclude_type', params.exclude_type);
  if (params?.product_role) queryParams.append('product_role', params.product_role);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.includeCategories) queryParams.append('include_categories', 'true');
  if (params?.custom_fields) queryParams.append('custom_fields', params.custom_fields);
  if (params?.tags) queryParams.append('tags', params.tags);
  if (params?.tags_mode) queryParams.append('tags_mode', params.tags_mode);
  if (params?.admin_scope) queryParams.append('admin_scope', params.admin_scope);
  if (params?.company_id) queryParams.append('company_id', params.company_id);
  if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params?.sort_direction) queryParams.append('sort_direction', params.sort_direction);
  if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params?.offset) queryParams.append('offset', String(params.offset));
  return `/api/v1/master-data/products/products${queryParams.toString() ? `?${queryParams}` : ''}`;
}

/** Server response envelope for the products list — rows plus the filtered total. */
export interface ProductListPage {
  items: Product[];
  /** Rows matching the filters server-side, NOT the length of `items`. */
  total: number;
}

/* eslint-disable react-hooks/exhaustive-deps */
// Intentionally only tracking specific param fields to prevent unnecessary re-renders
function useStableProductParams(params?: ListProductsParams) {
  return useMemo(
    () => cleanParams(params),
    [params?.category_id, params?.type, params?.exclude_type, params?.product_role, params?.status, params?.search, params?.includeCategories, params?.custom_fields, params?.tags, params?.tags_mode, params?.admin_scope, params?.company_id, params?.sort_by, params?.sort_direction, params?.limit, params?.offset]
  );
}
/* eslint-enable react-hooks/exhaustive-deps */

/**
 * The unbounded fetch — every product matching the filters, as a plain array.
 *
 * This is the picker/combo access pattern (§ 11 sanctions one endpoint carrying
 * both). A **server-paged table must use `useProductsPage`**, which is the only
 * one that surfaces the filtered `total` a pager needs.
 */
export function useProducts(params?: ListProductsParams) {
  const stableParams = useStableProductParams(params);

  return useQuery({
    queryKey: productKeys.list(stableParams),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: Product[] }>(
        buildProductListUrl(stableParams)
      );
      return response.data.data;
    },
  });
}

/**
 * The server-paged window: one page of rows plus the filtered `total`.
 *
 * Kept separate from `useProducts` rather than widening its return type — two
 * dozen pickers destructure `data` as a `Product[]`, and a paged table needs a
 * denominator none of them have any use for.
 */
export function useProductsPage(params: ListProductsParams) {
  const stableParams = useStableProductParams(params);

  return useQuery({
    queryKey: [...productKeys.list(stableParams), 'page'] as const,
    // Hold the current page's rows while the next one loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ProductListPage> => {
      const response = await axiosInstance.get<{ data: Product[]; total: number }>(
        buildProductListUrl(stableParams)
      );
      return { items: response.data.data, total: response.data.total };
    },
  });
}

export function useProduct(id: string, options?: { includeVariants?: boolean; includeConfigurations?: boolean; includeCategories?: boolean }) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.includeVariants) queryParams.append('include_variants', 'true');
      if (options?.includeConfigurations) queryParams.append('include_configurations', 'true');
      if (options?.includeCategories) queryParams.append('include_categories', 'true');

      const url = `/api/v1/master-data/products/products/${id}${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await axiosInstance.get<{ data: Product }>(url);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ProductCreate) => {
      const response = await axiosInstance.post<{ data: Product }>('/api/v1/master-data/products/products', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // A product can be created into a category — refresh category member counts.
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProductUpdate }) => {
      const response = await axiosInstance.put<{ data: Product }>(`/api/v1/master-data/products/products/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // product_category_ids may have changed — refresh category member counts so the
      // families list and pickers don't show a stale count.
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

/** Preview what deleting a {{product}} will affect (blockers / cascades / options). */
export function useProductDeletionImpact(id: string | null) {
  return useQuery({
    queryKey: [...productKeys.all, 'deletion-impact', id],
    queryFn: async () => {
      const response = await axiosInstance.get<DeletionImpact>(
        `/api/v1/master-data/products/products/${id}/deletion-impact`
      );
      return response.data;
    },
    enabled: !!id,
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, options }: { id: string; options?: Record<string, boolean> }) => {
      await axiosInstance.delete(`/api/v1/master-data/products/products/${id}`, {
        data: options ? { options } : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // Deleting a category member changes its category's count.
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
      // Deletion can zero-out / detach stock — refresh the inventory stock-levels list.
      queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

/**
 * Either an explicit id list or an escalation — never both, never neither.
 *
 * The server says exactly this with `EscalatableBulkRequest`'s `_exactly_one_scope`
 * validator, which rejects both-or-neither at 422. Spelling it as a union rather
 * than four optional fields moves that rejection to compile time: a caller that
 * forgets the scope cannot post `{ ids: undefined, status }` and quietly change
 * nothing.
 */
type BulkStatusScope =
  | { ids: string[]; matchAllFiltered?: never; filters?: never }
  /**
   * Act on every product matching `filters` rather than on named ids
   * (ui-list-pages.md §11). The server re-derives tenant, company and sharing
   * bounds itself, so these filters can only narrow.
   */
  | { matchAllFiltered: true; filters: ProductListFilterInput; ids?: never };

export function useBulkUpdateProductStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      status,
      matchAllFiltered,
      filters,
    }: BulkStatusScope & { status: ProductStatus }) => {
      const response = await axiosInstance.post<{ updated: number }>(
        '/api/v1/master-data/products/products/bulk-update',
        matchAllFiltered
          ? { match_all_filtered: true, status, filters }
          : { ids, status }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useBulkDeleteProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, options }: { ids: string[]; options?: Record<string, boolean> }) => {
      const response = await axiosInstance.post<{ deleted: number }>(
        '/api/v1/master-data/products/products/bulk-delete',
        { ids, options }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      // Deletion can zero-out / detach stock — refresh the inventory stock-levels list.
      queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });
}

export function useArchiveProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axiosInstance.post<{ data: Product }>(`/api/v1/master-data/products/products/${id}/archive`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

// Variant Hooks

export function useProductVariants(productId: string) {
  return useQuery({
    queryKey: productKeys.variants(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<ProductVariantListResponse>(
        `/api/v1/master-data/products/products/${productId}/variants`
      );
      return response.data.data;
    },
    enabled: !!productId,
  });
}

export function useCreateVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: VariantCreate }) => {
      const response = await axiosInstance.post<ProductVariantResponse>(
        `/api/v1/master-data/products/products/${productId}/variants`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

/**
 * Create a named SKU sibling — a variant with no dimension axes.
 *
 * The flat counterpart of `useCreateVariant`. Rejected by the backend when the
 * product already has dimensions: a product is either purely dimensional or
 * purely flat (docs/modules/master-data/flat-variant-design.md).
 */
export function useCreateFlatVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      name,
      sku,
    }: {
      productId: string;
      name: string;
      /** Optional — the backend composes one from the product SKU when omitted. */
      sku?: string;
    }) => {
      const response = await axiosInstance.post<ProductVariantResponse>(
        `/api/v1/master-data/products/products/${productId}/variants/flat`,
        { name, sku }
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

export function useGenerateVariants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      combinations,
    }: {
      productId: string;
      combinations: VariantCombination[];
    }) => {
      const response = await axiosInstance.post<ProductVariantListResponse>(
        `/api/v1/master-data/products/products/${productId}/variants/generate`,
        { combinations }
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

export function useResolveVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      selections,
    }: {
      productId: string;
      /** Maps each variant dimension id to the chosen value id. */
      selections: Record<string, string>;
    }) => {
      const response = await axiosInstance.post<ProductVariantResponse>(
        `/api/v1/master-data/products/products/${productId}/variants/resolve`,
        { selections }
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    },
  });
}

export function useUpdateVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ variantId, data }: { variantId: string; data: VariantUpdate }) => {
      const response = await axiosInstance.put<ProductVariantResponse>(
        `/api/v1/master-data/products/variants/${variantId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteVariant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string) => {
      await axiosInstance.delete(`/api/v1/master-data/products/variants/${variantId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
